import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { api } from "../../../evergis/api";
import { setSelectedFeatures, setSelectedFeaturesTotalCounts } from "../../../ducks/selectFeatures/selectFeatures";
import { resetFeatureToShow } from "../../../ducks/showFeature/showFeature";
import { setTableSelectedFeature } from "../../../ducks/table/table";
import { getFeatureToShow } from "../../../ducks/showFeature/selectors";
import { getGeometryEditor } from "../../../ducks/geometryEditor/selectors";
import { getTableFilterConditions } from "../../../ducks/table/selectors";
import { getDateFilterConditions } from "../../../ducks/dateFilter/selectors";
import { featureId } from "../../../ducks/selectFeatures/utils";
import { useGeometrySelection } from "../../../hooks/geometrySelection/useGeometrySelection";
import { useLayersToSelect } from "./useLayersToSelect";
import { useSelectFeaturesLayerName } from "../../../hooks/selectFeatures/useSelectFeaturesLayerName";
import { useTableSelection } from "../../../hooks/table/useTableSelection";
import { useDataSourceCondition } from "../../../hooks/layer/useDataSourceCondition";
import { useIsDashboard } from "../../../hooks/location/useIsDashboard";
import { useLocationQueryFeature } from "../../../ducks/selectFeatures/useLocationQueryFeature";
import { useEwktGeometry } from "./useEwktGeometry";
import { useGeometrySelectionConditions } from "../../../hooks/geometrySelection/useGeometrySelectionConditions";

import { FeatureDcExtended } from "../../../ducks/selectFeatures/types";
import { FilterConditionOperation } from "../../../components/LayerFilter/types";

// eslint-disable-next-line max-lines-per-function
export const useSelectedFeaturesEffect = () => {
  const dispatch = useDispatch();
  const isDashboard = useIsDashboard();
  const initialFeatures = useRef<string[] | null>(null);
  const featureToShow = useSelector(getFeatureToShow);
  const [{ history, settings, isBuffer, type }, setGeometrySelection] = useGeometrySelection();
  const ewktGeometry = useEwktGeometry();
  const { layerNames, wmsLayers } = useLayersToSelect();
  const geometrySelectionConditions = useGeometrySelectionConditions();
  const tableFilterConditions = useSelector(getTableFilterConditions);
  const dateFilterConditions = useSelector(getDateFilterConditions);
  const getDashboardCondition = useDataSourceCondition();
  const { active: geometryEditorActive } = useSelector(getGeometryEditor);
  const [, setSelectFeaturesLayerName] = useSelectFeaturesLayerName();
  const [, setTableSelection] = useTableSelection();

  useEffect(() => {
    dispatch(setSelectedFeatures({}));
    setTableSelection(undefined);

    if (history.features.length > 0 && history.step > -1) {
      setSelectFeaturesLayerName(undefined);

      setGeometrySelection({
        loading: true,
      });

      api.layers
        .getBulkFeatures([
          ...layerNames.map(layerName => {
            const condition = [
              geometrySelectionConditions?.[layerName],
              tableFilterConditions?.[layerName],
              dateFilterConditions?.[layerName],
              ...(isDashboard ? getDashboardCondition(layerName) : []),
            ]
              .filter(Boolean)
              .join(` ${FilterConditionOperation.AND} `);

            return {
              layerName,
              query: condition,
              limit: 200,
              ewktGeometry,
            };
          }),
          ...wmsLayers.map(layerName => {
            return {
              layerName,
              query: "",
              limit: 200,
              ewktGeometry,
            };
          }),
        ])
        .then(result => {
          const features = result.reduce((acc: Record<string, FeatureDcExtended[]>, curr) => {
            return {
              ...acc,
              [curr.layerName]: curr.items?.map(item => ({ ...item, layer: curr.layerName })) as FeatureDcExtended[],
            };
          }, {});

          const notNullLayersCount = Object.entries(features).reduce(
            (acc, [, value]) => acc + Number(value.length > 0),
            0,
          );

          if (notNullLayersCount > 0) {
            const firstNotNullLayerName = Object.keys(features).find(key => features[key].length > 0);
            const featureIdsToSelect = features[firstNotNullLayerName]?.map(featureId);

            if (initialFeatures.current === null) {
              initialFeatures.current = featureIdsToSelect;
            }

            dispatch(setSelectedFeatures(features));
            dispatch(setTableSelectedFeature(undefined));
          } else if (notNullLayersCount === 0 && featureToShow) {
            dispatch(resetFeatureToShow());
            dispatch(setTableSelectedFeature(undefined));
          } else if (notNullLayersCount === 0 && !featureToShow && Object.keys(features).length) {
            setSelectFeaturesLayerName(Object.keys(features)[0]);
          }

          dispatch(
            setSelectedFeaturesTotalCounts(
              result.reduce((acc: Record<string, number>, curr) => {
                return {
                  ...acc,
                  [curr.layerName]: curr.totalCount,
                };
              }, {}),
            ),
          );
        })
        .finally(() => {
          setGeometrySelection({
            loading: false,
          });
        });
    }
  }, [history.features, history.step, type, settings, isBuffer, tableFilterConditions, dateFilterConditions]);

  useLocationQueryFeature();

  useEffect(() => {
    setGeometrySelection({
      active: !geometryEditorActive,
    });
  }, [geometryEditorActive]); // eslint-disable-line
};
